import React, { useState } from "react";
import { fetchWithAuth, useUser } from "../Context/UserContext";
import { useNavigate } from "react-router-dom";
import { useCompany } from "../Context/CompanyContext"; // Importamos el contexto de empresas
import { LoadingOverlay } from "../components/ui/loading-overlay";
import API_URL from "../config";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const Associate_bs = (props) => {
  const { fetchUserInfo } = useUser(); // Obtenemos la información del usuario autenticado
  const { fetchCompanies } = useCompany(); // Obtenemos la función fetchCompanies del contexto
  const [selectedOption, setSelectedOption] = useState(null);
  const [submissionMessage, setSubmissionMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const [companyInfo, setCompanyInfo] = useState({
    id_company: null,
    ruc: "",
    nombre_empresa: "",
    sector: "",
  });

  const displayMessage = (message, type) => {
    setSubmissionMessage(message);
    setMessageType(type);
    setShowSuccessMessage(type === "success");

    // Asegurarse de limpiar cualquier temporizador anterior
    if (window.messageTimeout) {
      clearTimeout(window.messageTimeout);
    }

    // Ocultar el mensaje después de 3 segundos
    window.messageTimeout = setTimeout(() => {
      setSubmissionMessage("");
      setShowSuccessMessage(false);
    }, 3000); // 3000 ms = 3 segundos
  };

  const [companyUserInfo, setCompanyUserInfo] = useState({
    puesto: "",
    tipo_cuenta: "", // El que registra será Administrador
    area: "",
  });

  const resetCompanyInfo = () => {
    setCompanyInfo({
      id_company: "",
      ruc: "",
      nombre_empresa: "",
      sector: "",
    });

    setCompanyUserInfo({
      puesto: "",
      tipo_cuenta: "usuario",
      area: "",
    });
  };

  const handleOptionChange = (option) => {
    if (option === "register") {
      navigate("/company-register");
    } else if (option === "associate") {
      setSelectedOption("associate");
    }
    setSubmissionMessage("");
    setMessageType("");
  };

  const handleCompanyInfoChange = (e) => {
    const { name, value } = e.target;

    // Validación para asegurarse de que solo números sean permitidos en el campo RUC
    if (name === "ruc" && (isNaN(value) || value.length > 11)) {
      return; // Ignorar la entrada si no es un número o si excede los 11 dígitos
    }

    setCompanyInfo((prevInfo) => ({
      ...prevInfo,
      [name]: value ?? "",
    }));
  };

  const handleCompanyUserInfoChange = (e) => {
    const { name, value } = e.target;
    setCompanyUserInfo((prevInfo) => ({
      ...prevInfo,
      [name]: value ?? "",
    }));
  };

  const handleBack = () => {
    setSelectedOption(null);
    resetCompanyInfo();
  };

  const handleSearchCompany = async (ruc) => {
    setIsLoading(true); // 🔹 Activa el cargando
    const currentUserId = localStorage.getItem("current_user_id");
    const token = localStorage.getItem(`access_token_${currentUserId}`);

    if (!ruc || ruc.length !== 11 || !token) {
      displayMessage("Por favor ingresa un RUC válido de 11 dígitos.", "error");
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/bs/get_company_by_ruc?ruc=${ruc}`,
        { method: "GET" }
      );

      if (response.ok) {
        const data = await response.json();
        setCompanyInfo((prevInfo) => ({
          ...prevInfo,
          nombre_empresa: data.nombre_empresa,
          sector: data.sector,
          id_company: data.id_company,
        }));
      } else {
        const errorData = await response.json();
        displayMessage(errorData.error || "Empresa no encontrada.", "error");
      }
    } catch (error) {
      displayMessage(
        "Error al buscar la empresa. Intenta nuevamente.",
        "error"
      );
    } finally {
      setIsLoading(false); // 🔹 Desactiva el cargando al final
    }
  };

  const handleAssociateCompanySubmit = async (e, url) => {
    setIsLoading(true); // 🔹 Activa el cargando
    e.preventDefault();

    // Validación de RUC
    if (!companyInfo.ruc || companyInfo.ruc.length !== 11) {
      displayMessage("Por favor ingresa un RUC válido de 11 dígitos.", "error");
      return;
    }

    // Validación de puesto
    if (!companyUserInfo.puesto) {
      displayMessage("Por favor ingresa tu puesto en la empresa.", "error");
      return;
    }

    // Validación de puesto
    if (!companyUserInfo.area) {
      displayMessage("Por favor ingresa tu area dentro la empresa.", "error");
      return;
    }
    const currentUserId = localStorage.getItem("current_user_id");
    const token = localStorage.getItem(`access_token_${currentUserId}`);

    if (token) {
      try {
        const response = await fetchWithAuth(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ruc: companyInfo.ruc,
            puesto: companyUserInfo.puesto,
            area: companyUserInfo.area,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          displayMessage("Solicitud enviada exitosamente.", "success");

          if (props.onCompanyRegistered) {
            props.onCompanyRegistered(data.company);
          }
          // 🔄 Recargar la página después de una respuesta exitosa
          setTimeout(() => {
            window.location.reload();
          }, 500); // Espera 1 segundo para mostrar el mensaje antes de recargar
        } else {
          const errorData = await response.json();
          displayMessage(
            errorData.error || "Error al enviar la solicitud.",
            "error"
          );
        }
      } catch (error) {
        displayMessage("Error al enviar la solicitud.", "error");
      } finally {
        setIsLoading(false); // 🔹 Desactiva el cargando al final
      }
    } else {
      displayMessage("Token no encontrado.", "error");
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingOverlay />;
  }

  return (
    <div>
      {selectedOption === null ? (
        <div>
          <DialogHeader>
            <DialogTitle>Selecciona una opción</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button className="flex-1" onClick={() => handleOptionChange("register")}>
              Registrar nueva empresa
            </Button>
            <Button className="flex-1" variant="outline" onClick={() => handleOptionChange("associate")}>
              Empresa ya creada
            </Button>
          </div>
        </div>
      ) : selectedOption === "associate" ? (
        <form onSubmit={(e) => handleAssociateCompanySubmit(e, `${API_URL}/bs/associate_company`)}>
          {/* Formulario para asociarse */}
          {!companyInfo.nombre_empresa && (
            <div>
              <DialogHeader>
                <DialogTitle>Busca la empresa con su RUC</DialogTitle>
              </DialogHeader>
              <div>
                <label htmlFor="ruc" className="mb-1.5 block text-sm font-medium text-foreground">
                  RUC
                </label>
                <Input
                  type="number"
                  id="ruc"
                  name="ruc"
                  value={companyInfo.ruc || ""}
                  onChange={handleCompanyInfoChange}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleBack}>
                  Regresar
                </Button>
                <Button type="button" onClick={() => handleSearchCompany(companyInfo.ruc)}>
                  Buscar
                </Button>
              </DialogFooter>
            </div>
          )}
          {/* Mostrar la información de la empresa encontrada */}
          {companyInfo.nombre_empresa && (
            <div>
              <DialogHeader>
                <DialogTitle>Empresa encontrada</DialogTitle>
              </DialogHeader>
              <div className="space-y-1 text-sm text-muted-foreground">
                <p>
                  <strong className="text-foreground">Nombre:</strong> {companyInfo.nombre_empresa}
                </p>
                <p>
                  <strong className="text-foreground">Sector:</strong> {companyInfo.sector}
                </p>
                <p>
                  <strong className="text-foreground">RUC:</strong> {companyInfo.ruc}
                </p>
              </div>
              <div className="mt-4">
                <p className="text-sm text-muted-foreground">
                  Si deseas asociarte, ingresa tu puesto y manda la solicitud:
                </p>
                <div className="mt-2 space-y-3">
                  <div>
                    <label htmlFor="puesto" className="mb-1.5 block text-sm font-medium text-foreground">
                      Puesto:
                    </label>
                    <Input
                      type="text"
                      id="puesto"
                      name="puesto"
                      value={companyUserInfo.puesto}
                      onChange={handleCompanyUserInfoChange}
                      placeholder="Ingrese su puesto"
                      maxLength={50} // Máximo 50 caracteres
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="area" className="mb-1.5 block text-sm font-medium text-foreground">
                      Area:
                    </label>
                    <Input
                      type="text"
                      id="area"
                      name="area"
                      value={companyUserInfo.area}
                      onChange={handleCompanyUserInfoChange}
                      placeholder="Ingrese su area"
                      maxLength={50} // Máximo 50 caracteres
                      required
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={handleBack}>
                    Regresar
                  </Button>
                  <Button
                    type="submit"
                    onClick={(e) => handleAssociateCompanySubmit(e, `${API_URL}/bs/associate_company`)}
                  >
                    Mandar solicitud
                  </Button>
                </DialogFooter>
              </div>
            </div>
          )}
        </form>
      ) : null}
      {/* Mensajes de éxito o error */}
      {showSuccessMessage && (
        <p className="mt-3 text-sm font-semibold text-green-600">{submissionMessage}</p>
      )}
      {submissionMessage && !showSuccessMessage && (
        <p className={`mt-3 text-sm font-semibold ${messageType === "error" ? "text-destructive" : "text-foreground"}`}>
          {submissionMessage}
        </p>
      )}
    </div>
  );
};

export default Associate_bs;
